import { useTranslate } from "@similarweb/i18n-provider";
import { ErrorBox } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Button, PixelPlaceholderLoader } from "@similarweb/ui-components";

import signalRecommenderI18nKeys from "../../utils/i18n";

import { StyledResultRow, StyledResultRowMessage } from "./styles";
import type { FilterPanelDescriptor } from "./types";

/** Matches the trigger card's height so the section reserves its space from the first paint. */
const PANEL_SKELETON_HEIGHT_PX = 46;

type FilterPanelSlotProps = {
    descriptor: FilterPanelDescriptor;
};

export const FilterPanelSlot = ({ descriptor }: FilterPanelSlotProps) => {
    const { translate } = useTranslate();

    const { title, dataAutomation, state } = descriptor;
    const filterError = signalRecommenderI18nKeys.confirm.filters.error({ filter: title });

    switch (state.status) {
        case "pending":
            return (
                <PixelPlaceholderLoader
                    id={`${dataAutomation}-loading`}
                    width="100%"
                    height={PANEL_SKELETON_HEIGHT_PX}
                />
            );

        case "error":
            // Inline rather than a modal-level banner: the categories fail independently, so only a
            // message in the gap itself can say which one is missing. Submitting stays available —
            // filters narrow an enrichment, they are not a precondition for one.
            return (
                <StyledResultRow
                    data-automation={`${dataAutomation}-error`}
                    isError
                >
                    <ErrorBox
                        size="xs"
                        color={colorsPalettes.red[400]}
                    />
                    <StyledResultRowMessage
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        whiteSpace="normal"
                    >
                        {translate(filterError.key, filterError.replacements)}
                    </StyledResultRowMessage>
                    <Button
                        type="flatDark"
                        onClick={state.onRetry}
                        dataAutomation={`${dataAutomation}-retry`}
                    >
                        {translate("si.common.button.retry")}
                    </Button>
                </StyledResultRow>
            );

        case "success":
            return <div>{state.renderPanel()}</div>;
    }
};
